import {
    AdminUserByIdResponse,
    GetAdminUsersInput,
    GetAdminUsersResponse,
    MemberEditUser,
    PutAdminUserInput,
} from "../../../types/api/admin/user";
import { Role } from "../../../types/enums/Role";
import { UserAdminResult } from "../../../types/User";
import { getFormattedDateInput, formatProSinceInputValue } from "../../../utils/date";
import { getAllRoles } from "./role";
import { request } from "../../apiService";

export const getUsers = async (input: GetAdminUsersInput) => {
    return await request<GetAdminUsersResponse, GetAdminUsersInput>(
        `/v3/admin/users`,
        {
            input,
        }
    );
};

export const getUserById = async (id: string) => {
    return await request<AdminUserByIdResponse, { id: string }>(
        `/v3/admin/users/${id}`,
    );
};

const MEMBER_DETAILS_CACHE_PREFIX = "adminMemberDetails:";

export const getMemberDetailsCacheKey = (userId: string) =>
    `${MEMBER_DETAILS_CACHE_PREFIX}${userId}`;

export const cacheMemberDetailsUser = (user: UserAdminResult) => {
    try {
        sessionStorage.setItem(
            getMemberDetailsCacheKey(user.id),
            JSON.stringify(user),
        );
    } catch {
        // Ignore quota / private mode errors.
    }
};

const readMemberDetailsCache = (userId: string): UserAdminResult | null => {
    try {
        const raw = sessionStorage.getItem(getMemberDetailsCacheKey(userId));
        if (!raw) return null;
        return JSON.parse(raw) as UserAdminResult;
    } catch {
        return null;
    }
};

const buildMemberDetailsListInput = (
    search: string,
    activityStatus: GetAdminUsersInput["activityStatus"] = "all",
): GetAdminUsersInput => ({
    search,
    startDate: getFormattedDateInput(new Date(2023, 0, 1)),
    endDate: getFormattedDateInput(new Date()),
    sortBy: "pseudo",
    sortOrder: "asc",
    activityStatus,
});

const mapProfileToMemberDetailsUser = (
    profile: AdminUserByIdResponse,
): UserAdminResult => ({
    id: profile.id,
    key: profile.id,
    discordId: "",
    discordName: "",
    pseudo: profile.pseudo ?? "",
    firstname: profile.firstname ?? "",
    lastname: profile.lastname ?? "",
    email: profile.email ?? "",
    role: (profile.role as Role) ?? Role.Junior,
    roleId: profile.roleId ?? "",
    createdAt: new Date(),
    proSince: profile.proSince ?? null,
    permissions: [],
    isActive: profile.isActive ?? true,
    enrollmentDate: profile.enrollmentDate,
    preferredTimeslot: profile.preferredTimeslot,
    lastMindset: profile.lastMindset,
    isFavorite: false,
    performance_percentage: 0,
    performance_total: 0,
    questions: profile.questions ?? [],
    notes: [],
});

const buildMemberDetailsSearchTerms = (
    profile: AdminUserByIdResponse,
    userId: string,
) => {
    const terms = new Set<string>([userId]);
    if (profile.pseudo?.trim()) terms.add(profile.pseudo.trim());
    if (profile.email?.trim()) terms.add(profile.email.trim());

    const fullName = [profile.firstname, profile.lastname]
        .map((value) => value?.trim())
        .filter(Boolean)
        .join(" ");
    if (fullName) terms.add(fullName);

    return Array.from(terms);
};

const findUserInAdminList = async (
    profile: AdminUserByIdResponse,
    userId: string,
): Promise<UserAdminResult | null> => {
    const searchTerms = buildMemberDetailsSearchTerms(profile, userId);
    const activityStatuses: NonNullable<GetAdminUsersInput["activityStatus"]>[] =
        ["all", "active", "inactive"];

    for (const search of searchTerms) {
        for (const activityStatus of activityStatuses) {
            const users = await getUsers(
                buildMemberDetailsListInput(search, activityStatus),
            );
            const match = users?.find((user) => user.id === userId);
            if (match) return match;
        }
    }

    return null;
};

/** Charge un membre pour MemberDetails (questions + métadonnées comme dans le listing admin). */
export const fetchMemberDetailsUser = async (
    userId: string,
): Promise<UserAdminResult | null> => {
    const cached = readMemberDetailsCache(userId);
    if (cached) return cached;

    const profile = await getUserById(userId);
    if (!profile?.id) return null;

    const fromList = await findUserInAdminList(profile, userId);
    if (fromList) {
        cacheMemberDetailsUser(fromList);
        return fromList;
    }

    const member = mapProfileToMemberDetailsUser(profile);
    if (member.questions.length > 0 || member.proSince) {
        cacheMemberDetailsUser(member);
    }

    return member;
};

type MemberEditSource = {
    id: string;
    firstname?: string | null;
    lastname?: string | null;
    email?: string | null;
    roleId?: string;
    isActive?: boolean;
    proSince?: Date | string | null;
    currentRoleSince?: Date | string | null;
};

/** Normalise un profil membre pour MemberModal. */
export const mapToMemberEditUser = (
    source: MemberEditSource,
): MemberEditUser | null => {
    const email = source.email?.trim() ?? "";
    const roleId = source.roleId ?? "";

    if (!email || !roleId) {
        return null;
    }

    const roleSince = formatProSinceInputValue(
        source.currentRoleSince ?? source.proSince,
    );

    return {
        id: source.id,
        firstname: (source.firstname ?? "").trim(),
        lastname: (source.lastname ?? "").trim(),
        email,
        roleId,
        isActive: typeof source.isActive === "boolean" ? source.isActive : true,
        roleSince,
    };
};

/** Charge et normalise un utilisateur pour MemberModal (rôle, email, compte actif). */
export const fetchUserForMemberEdit = async (
    userId: string,
    roles?: { id: string; name: string }[],
): Promise<MemberEditUser | null> => {
    const raw = await getUserById(userId);
    if (!raw?.id) return null;

    const roleList = roles?.length ? roles : (await getAllRoles()) ?? [];
    const roleId =
        raw.roleId ?? roleList.find((r) => r.name === raw.role)?.id ?? "";

    return mapToMemberEditUser({
        id: raw.id,
        firstname: raw.firstname,
        lastname: raw.lastname,
        email: raw.email,
        roleId,
        isActive: raw.isActive,
        proSince: raw.proSince,
        currentRoleSince: raw.currentRoleSince,
    });
};

export const getUsersCSV = async (input: GetAdminUsersInput) => {
    return await request(`/v3/admin/users/extract`, {
        input,
    });
};

export const updateUserById = async (id: string, input: PutAdminUserInput) => {
    return await request(`/v2/admin/users/${id}`, {
        method: "PUT",
        input,
    });
};
